import { returnFiletypeIcon } from "@/lib/icon";
import { cn } from "@/lib/utils";
import { FileIcon } from "lucide-react"
import { useEffect, useId, useState } from "react"

type ReturnObject = {
    base64: string;
    filename: string;
}

type FileUploaderProps = {
    onFileUpload?: (result:ReturnObject) => void;
    fileValue?: string; // Base64 string to set the initial/default file
    fileName?: string; // File name to set the initial/default file
    maxFileSizeMB?: number;
    allowedTypes?: string[];
    placeholder?: string
}

const FileUploader = ({
    onFileUpload,
    fileValue,
    fileName,
    maxFileSizeMB = 2, // Default max file size 2MB
    allowedTypes = ["*"],
    className,
    placeholder = 'Upload a file here',
    disabled,
    ...props
}:FileUploaderProps & React.InputHTMLAttributes<HTMLInputElement>) => {
    
    const id = useId()
    const [preview, setPreview] = useState<string | null>(fileValue || null);
    const [filename, setFilename] = useState<string | null>(fileName ||null);
    const [error, setError] = useState<string | null>(null);
    const [isDragging, setIsDragging] = useState(false);
    const [inputKey, setInputKey] = useState(0); // to force re-render

    useEffect(() => {
        if (fileValue) {
          setPreview(fileValue); // Update preview if fileValue changes
        }
    }, [fileValue]);

    useEffect(()=>{
        if(fileName) {
            setFilename(fileName)
        }
    }, [fileName])

    useEffect(() => {
        if (filename) {
          setFilename(filename); // Update filename if filename changes
        }
    }, [filename]);

    function getFileExtension(filename?: string | null) {
        if(!filename) return;
        return filename.split('.').pop()?.toLowerCase() || '';
    }

    const handleFileUpload = (file: File) => {
        if(disabled) return;
        if (file) {
            // Check file type
            const fileExt = getFileExtension(file.name);
            const mimeType = file.type;
            const allowed = allowedTypes.includes('*') || allowedTypes.some(type => mimeType.includes(type) || fileExt?.includes(type));

            if (!allowed) {
                setError(`File type not supported. Allowed types: ${allowedTypes.join(", ")}`);
                return;
            }

            // Check file size
            if (file.size / 1024 / 1024 > maxFileSizeMB) {
                setError(`File size exceeds ${maxFileSizeMB}MB.`);
                return;
            }
        
            const reader = new FileReader();
            reader.onloadend = () => {
                const base64String = reader.result as string;
                setPreview(base64String);
                setFilename(file.name);
                setError(null); // Clear error
                if (onFileUpload) {
                    onFileUpload({ base64: base64String, filename: file.name }); // Pass base64 string and filename
                }
            };
            reader.readAsDataURL(file); // Convert to base64
        }
    };
    
    const handleRemoveImage = () => {
        if(disabled) return;
        setPreview(null);
        setFilename(null);
        setError(null);
        setInputKey(prev => prev + 1);
        onFileUpload && onFileUpload({ base64: '', filename: '' });
    };

    const handleDragOver = (e: React.DragEvent<HTMLLabelElement>) => {
        if(disabled) return;
        e.preventDefault();
        setIsDragging(true); // Indicate dragging state
    };
    
    const handleDragLeave = () => {
        if(disabled) return;
        setIsDragging(false); // Reset dragging state
    };
    
    const handleDrop = (e: React.DragEvent<HTMLLabelElement>) => {
        if(disabled) return;
        e.preventDefault();
        setIsDragging(false); // Reset dragging state
        const file = e.dataTransfer?.files?.[0];
        if (file) handleFileUpload(file);
    };
    
    return (
        <label htmlFor={id} className={cn(
            isDragging && "bg-neutral-100",
            "flex items-center justify-between p-3 border border-dashed rounded-md cursor-pointer border-neutral-300 group")} onDragOver={(e)=>{handleDragOver(e)}} onDragLeave={handleDragLeave} onDrop={(e)=>{handleDrop(e)}}>
            
            <input key={inputKey} disabled={disabled} type="file" hidden accept={allowedTypes.join(",")} id={id} {...props} onChange={(e)=>{ if(e.target.files){ handleFileUpload(e.target.files?.[0]) } }}  />
            <div className="flex items-center gap-3">
                {preview 
                ? <img src={returnFiletypeIcon(getFileExtension(filename))} alt="preview" className="object-cover w-8 h-8" />
                : <div className="flex items-center justify-center w-10 h-10 overflow-hidden rounded-sm bg-neutral-50"><FileIcon className="w-4 h-4 text-neutral-500"/></div>
                }
                
                <div className="flex flex-col overflow-hidden">
                    <p className="text-sm truncate text-neutral-500">{filename ? filename : placeholder}</p>
                    <p className="text-xs truncate text-neutral-500">Maximum {maxFileSizeMB}MB</p>
                    {error && <p className="text-sm font-medium text-red-600 truncate">{error}</p>}
                </div>
            </div>
            
            {!disabled && <>
                {preview 
                ? <button onClick={(e)=>{e.preventDefault(); handleRemoveImage()}} className="px-2 py-1 text-sm font-medium text-red-600 border rounded-full border-neutral-200 hover:bg-red-100 hover:border-red-200">Delete</button>
                : <div className="px-2 py-1 text-sm font-medium border rounded-full border-neutral-200 text-neutral-600 hover:bg-neutral-100 group-hover:bg-neutral-100">Browse</div>
                }
            </>}
        </label>
    )
}

export { FileUploader }